import {
  type CanvasMetadata,
  MetricName,
  type MetricNameValue,
  ReliabilityMetrics
} from '@canvas/frontend-metrics';

import { buildDatabaseSizeMetadata } from '../build-database-size-metadata.ts';

import { type OverlayRow, showOverlayComplete } from './overlay.ts';
import { isActionTracingEnabled } from './storage.ts';

interface PendingTrace {
  id: string;
  action: DatabaseTraceAction;
  source: DatabaseTraceSource;
  label: string;
  t0: number;
  t1: number | null;
  t2: number | null;
  t3: number | null;
  transactionCount: number;
  wasBackgrounded: boolean;
  debugEnabled: boolean;
  contentId?: string;
}

interface CompleteTrace extends PendingTrace {
  t1: number;
  t2: number;
  t3: number;
}

export enum DatabaseTraceAction {
  EntryAdd = 'entry.add',
  EntryClear = 'entry.clear',
  EntryDelete = 'entry.delete',
  EntryDuplicate = 'entry.duplicate',
  EntryInsertAfter = 'entry.insert.after',
  EntryInsertBefore = 'entry.insert.before',
  EntryMoveDown = 'entry.move.down',
  EntryMoveUp = 'entry.move.up',
  FieldAdd = 'field.add',
  FieldClear = 'field.clear',
  FieldDelete = 'field.delete',
  FieldDuplicate = 'field.duplicate',
  FieldInsertAfter = 'field.insert.after',
  FieldInsertBefore = 'field.insert.before',
  FieldMoveLeft = 'field.move.left',
  FieldMoveRight = 'field.move.right',
  ValueClear = 'value.clear'
}

export enum DatabaseTraceSource {
  ActionBar = 'action-bar',
  EntryMenu = 'entry-menu',
  EntryPanel = 'entry-panel',
  FieldHeader = 'field-header',
  FieldMenu = 'field-menu',
  Table = 'table',
  ValueMenu = 'value-menu'
}

export interface DatabaseTraceDescriptor {
  action: DatabaseTraceAction;
  source: DatabaseTraceSource;
}

let activeTrace: PendingTrace | null = null;
let nextIdCounter = 0;

function nextId(): string {
  nextIdCounter = (nextIdCounter + 1) % 0xffff;
  return nextIdCounter.toString(16).padStart(4, '0');
}

function toLabel({ action, source }: DatabaseTraceDescriptor): string {
  return `${action} (${source})`;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled database trace action: ${value}`);
}

function metricNameForMutation(action: DatabaseTraceAction): MetricNameValue {
  switch (action) {
    case DatabaseTraceAction.EntryAdd:
    case DatabaseTraceAction.EntryClear:
    case DatabaseTraceAction.EntryDelete:
    case DatabaseTraceAction.EntryDuplicate:
    case DatabaseTraceAction.EntryInsertAfter:
    case DatabaseTraceAction.EntryInsertBefore:
    case DatabaseTraceAction.EntryMoveDown:
    case DatabaseTraceAction.EntryMoveUp:
      return MetricName.DATABASE_ENTRY_MUTATION_TIME;

    case DatabaseTraceAction.FieldAdd:
    case DatabaseTraceAction.FieldClear:
    case DatabaseTraceAction.FieldDelete:
    case DatabaseTraceAction.FieldDuplicate:
    case DatabaseTraceAction.FieldInsertAfter:
    case DatabaseTraceAction.FieldInsertBefore:
    case DatabaseTraceAction.FieldMoveLeft:
    case DatabaseTraceAction.FieldMoveRight:
      return MetricName.DATABASE_FIELD_MUTATION_TIME;

    case DatabaseTraceAction.ValueClear:
      return MetricName.DATABASE_VALUE_MUTATION_TIME;

    default:
      return assertNever(action);
  }
}

function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') {
    return true;
  }
  return document.visibilityState === 'visible';
}

function trackVisibility(trace: PendingTrace): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const markBackgrounded = () => {
    if (!isDocumentVisible()) {
      trace.wasBackgrounded = true;
    }
  };

  markBackgrounded();
  document.addEventListener('visibilitychange', markBackgrounded);
  return () => document.removeEventListener('visibilitychange', markBackgrounded);
}

function toOverlayRow(trace: PendingTrace, status: OverlayRow['status']): OverlayRow {
  const actionToYjsMs = trace.t1 !== null ? trace.t1 - trace.t0 : null;
  const yjsToReduxMs =
    trace.t1 !== null && trace.t2 !== null ? trace.t2 - trace.t1 : null;
  const reduxToPaintMs =
    trace.t2 !== null && trace.t3 !== null ? trace.t3 - trace.t2 : null;
  const totalMs =
    trace.t3 !== null
      ? trace.t3 - trace.t0
      : trace.t2 !== null
        ? trace.t2 - trace.t0
        : trace.t1 !== null
          ? trace.t1 - trace.t0
          : null;
  return {
    id: trace.id,
    label: trace.label,
    status,
    actionToYjsMs,
    yjsToReduxMs,
    reduxToPaintMs,
    totalMs,
    transactionCount: trace.transactionCount
  };
}

function buildTraceMetadata(trace: CompleteTrace): CanvasMetadata {
  const baseMetadata = {
    ...(trace.contentId ? { contentId: trace.contentId } : {}),
    interactionTimingSource: 'database-mutation-tracer',
    action: trace.action,
    source: trace.source,
    transactionCount: trace.transactionCount,
    actionToYjsMs: trace.t1 - trace.t0,
    yjsToReduxMs: trace.t2 - trace.t1,
    reduxToPaintMs: trace.t3 - trace.t2
  };

  return {
    ...baseMetadata,
    ...buildDatabaseSizeMetadata(baseMetadata)
  };
}

function isCompleteTrace(
  trace: PendingTrace,
  status: OverlayRow['status']
): trace is CompleteTrace {
  return (
    status === 'complete' && trace.t1 !== null && trace.t2 !== null && trace.t3 !== null
  );
}

function publishMutationPerformance(
  trace: PendingTrace,
  status: OverlayRow['status']
): void {
  if (!isCompleteTrace(trace, status)) {
    return;
  }

  void ReliabilityMetrics.getInstance().successWithDuration(
    metricNameForMutation(trace.action),
    trace.t0,
    trace.t3,
    buildTraceMetadata(trace)
  );
}

function finalize(trace: PendingTrace, status: OverlayRow['status']): void {
  if (trace.wasBackgrounded) {
    return;
  }
  publishMutationPerformance(trace, status);
  if (!trace.debugEnabled) {
    return;
  }
  showOverlayComplete(toOverlayRow(trace, status));
  if (typeof performance.mark === 'function') {
    try {
      const endName = `perf-tracing:end:${trace.id}:${trace.label}`;
      const startName = `perf-tracing:start:${trace.id}:${trace.label}`;
      performance.mark(endName);
      if (typeof performance.measure === 'function') {
        performance.measure(
          `perf-tracing: ${trace.label} (${status})`,
          startName,
          endName
        );
      }
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch {
      // No-op if marks were cleared or unsupported.
    }
  }
}

/**
 * Wrap the synchronous app-owned portion of a database action to capture debug
 * timings through the same phases a user waits on before seeing the completed
 * mutation reflected in the UI.
 *
 * The tracer observes Yjs/Redux without changing the Redux sync code. At
 * database bootstrap we register listeners in this order:
 *
 * ```text
 * Yjs doc.on('update', syncCollabChangesToRedux(...))  // existing listener
 * installPerfTracerForDatabase(collabManager, contentId)
 *   ├─ doc.on('afterTransaction', mark T1)
 *   └─ doc.on('update', mark T2)                       // registered second
 * ```
 *
 * Yjs invokes listeners for the same event synchronously in registration order.
 * The first `update` listener is the real Redux sync work: it calls
 * `syncCollabChangesToRedux`, computes the diff, and dispatches
 * `applyDatabaseDiff`. The second `update` listener is the tracer's
 * `onUpdateAfterRedux`; it runs only after the first listener returns and then
 * records T2 via `markReduxSyncComplete`.
 *
 * Expected synchronous local-write timeline:
 *
 * ```text
 * T0  traceDatabaseAction sets activeTrace and calls fn()
 *     └─ fn() performs synchronous app work and dispatches/applyChangeset
 *        └─ Yjs doc.transact(...)
 *
 * T1      Yjs afterTransaction listener runs during doc.transact(...)
 *         └─ markYjsTransactionCommit records the database contentId
 *            and first Yjs commit
 *
 *         Yjs emits update for the transaction
 *         ├─ existing syncCollabChangesToRedux update listener runs first
 *         │  └─ dispatch(applyDatabaseDiff(...)) returns synchronously
 * T2      └─ tracer update listener runs second
 *            └─ markReduxSyncComplete records post-Redux-sync time
 *
 *     fn() returns
 *     queueMicrotask closes activeTrace and checks for no-yjs
 *     double requestAnimationFrame waits for render/paint opportunity
 * T3  second rAF records the post-paint settling point
 * ```
 *
 * Phase model:
 *
 * ```text
 * user triggers database action
 *   └─ traceDatabaseAction(...) starts
 *        T0 = action handler begins
 *        │    includes synchronous app work inside the wrapper:
 *        │    selection resolution, validation, changeset construction,
 *        │    dispatch/applyChangeset, etc.
 *        ▼
 *      Yjs doc.transact(...)
 *        T1 = first Yjs afterTransaction observed
 *        │    actionToYjsMs = T1 - T0
 *        ▼
 *      syncCollabChangesToRedux update listener
 *        T2 = post-Redux-sync listener runs after applyDatabaseDiff dispatch
 *        │    yjsToReduxMs = T2 - T1
 *        ▼
 *      double requestAnimationFrame
 *        T3 = post-paint settling point
 *             reduxToPaintMs = T3 - T2
 *             totalMs = T3 - T0
 * ```
 *
 *
 * Production metric emission always measures completed foreground mutations. The
 * devtools toggle only controls the local overlay and Performance timeline marks.
 *
 * Place this wrapper around the code that actually executes the database
 * mutation, including synchronous preparation that contributes to action
 * latency. Do not wrap outer flows that only open a menu/dialog. For confirm
 * dialogs, wrap the confirmed mutation callback so user think time is excluded.
 *
 * Only synchronous handlers are fully timed. If the handler awaits before
 * dispatching, the outer trace will complete as `no-yjs` and the actual write
 * becomes untraced; wrap the synchronous dispatch/mutation portion instead.
 */
export function traceDatabaseAction<T>(
  descriptor: DatabaseTraceDescriptor,
  fn: () => T
): T {
  if (activeTrace !== null) {
    return fn();
  }

  const debugEnabled = isActionTracingEnabled();
  const trace: PendingTrace = {
    id: nextId(),
    action: descriptor.action,
    source: descriptor.source,
    label: toLabel(descriptor),
    t0: performance.now(),
    t1: null,
    t2: null,
    t3: null,
    transactionCount: 0,
    wasBackgrounded: false,
    debugEnabled
  };
  activeTrace = trace;
  const stopTrackingVisibility = trackVisibility(trace);
  // No in-flight overlay row: the main thread is blocked synchronously
  // inside fn() so the browser can't paint anything until after T2 — by
  // which time the trace is already on its way to "complete". We do drop a
  // `performance.mark` so the action shows up on the DevTools Performance
  // timeline if you're recording one.
  if (debugEnabled && typeof performance.mark === 'function') {
    try {
      performance.mark(`perf-tracing:start:${trace.id}:${trace.label}`);
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch
    } catch {
      // performance.mark with reserved names can throw; ignore.
    }
  }

  try {
    return fn();
  } finally {
    queueMicrotask(() => {
      activeTrace = null;
      if (trace.t1 === null) {
        stopTrackingVisibility();
        finalize(trace, 'no-yjs');
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (trace.t3 !== null) {
            return;
          }
          trace.t3 = performance.now();
          stopTrackingVisibility();
          finalize(trace, 'complete');
        });
      });
    });
  }
}

/**
 * Called from the Yjs `afterTransaction` listener installed by
 * `installPerfTracerForDatabase`. Synchronous re-entry into the active
 * trace's call stack means `activeTrace` is still set.
 */
export function markYjsTransactionCommit(
  transactionChangeCount: number,
  contentId: string
): void {
  if (activeTrace === null) {
    return;
  }
  activeTrace.contentId = contentId;
  activeTrace.transactionCount += 1;
  if (activeTrace.t1 === null) {
    activeTrace.t1 = performance.now();
  }
  // transactionChangeCount is kept available for future metadata; the
  // overlay currently displays the transaction count rather than per-tx
  // change counts to stay compact.
  void transactionChangeCount;
}

/**
 * Called from the post-`applyDatabaseDiff` listener installed by
 * `installPerfTracerForDatabase`. Marks the point at which the Redux
 * databases-slice has been updated for the user action's Yjs commit.
 */
export function markReduxSyncComplete(): void {
  if (activeTrace === null) {
    return;
  }
  if (activeTrace.t1 === null) {
    return;
  }
  if (activeTrace.t2 === null) {
    activeTrace.t2 = performance.now();
  }
}
/**
 * Test-only helper to reset module state between tests.
 */
export function __resetTracerForTests(): void {
  activeTrace = null;
  nextIdCounter = 0;
}
